import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { appointmentReminderJobService } from '../services/api';
import { toast } from 'react-hot-toast';

const EditAppointmentReminderJobEntryDialog = ({ isOpen, onClose, entry, onSave }) => {
  const [formData, setFormData] = useState({
    district_code: '',
    phone_number: '',
    patient_name: '',
    doctor_name: '',
    clinic_name: '',
    clinic_address: '',
    appointment_date: '',
    appointment_start_time: '',
    appointment_duration: '',
    reference_doctor_id: '',
    reference_clinic_id: '',
    reference_appointment_id: '',
    template_name: '',
    error_message: '',
    remarks: '',
    send_status: 'pending'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (isOpen && entry) {
      setFormData({
        district_code: entry.district_code || '',
        phone_number: entry.phone_number || '',
        patient_name: entry.patient_name || '',
        doctor_name: entry.doctor_name || '',
        clinic_name: entry.clinic_name || '',
        clinic_address: entry.clinic_address || '',
        appointment_date: entry.appointment_date || '',
        appointment_start_time: entry.appointment_start_time || '',
        appointment_duration: entry.appointment_duration || '',
        reference_doctor_id: entry.reference_doctor_id || '',
        reference_clinic_id: entry.reference_clinic_id || '',
        reference_appointment_id: entry.reference_appointment_id || '',
        template_name: entry.template_name || '',
        error_message: entry.error_message || '',
        remarks: entry.remarks || '',
        send_status: entry.send_status || 'pending'
      });
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen, entry]);

  const handleClose = () => {
    setFormData({
      district_code: '',
      phone_number: '',
      patient_name: '',
      doctor_name: '',
      clinic_name: '',
      clinic_address: '',
      appointment_date: '',
      appointment_start_time: '',
      appointment_duration: '',
      reference_doctor_id: '',
      reference_clinic_id: '',
      reference_appointment_id: '',
      template_name: '',
      error_message: '',
      remarks: '',
      send_status: 'pending'
    });
    setError(null);
    setValidationErrors({});
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.patient_name.trim()) {
      errors.patient_name = 'Patient name is required';
    }

    if (!formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required';
    }

    if (!formData.appointment_date) {
      errors.appointment_date = 'Appointment date is required';
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.appointment_date)) {
      errors.appointment_date = 'Date must be in YYYY-MM-DD format';
    }

    if (!formData.appointment_start_time) {
      errors.appointment_start_time = 'Appointment start time is required';
    } else if (!/^\d{2}:\d{2}$/.test(formData.appointment_start_time)) {
      errors.appointment_start_time = 'Time must be in HH:MM format';
    }

    if (!formData.appointment_duration || isNaN(formData.appointment_duration) || formData.appointment_duration <= 0) {
      errors.appointment_duration = 'Appointment duration must be a positive number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const entryData = {
        ...formData,
        appointment_duration: parseInt(formData.appointment_duration)
      };

      await appointmentReminderJobService.upsertAppointmentReminderJobEntry(
        entry.id,
        entry.job_id || entry.appointment_reminder_job_id,
        entry.clid,
        entryData
      );

      toast.success('Entry updated successfully');
      onSave(entryData);
      handleClose();
    } catch (err) {
      console.error('Error updating appointment reminder job entry:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update entry. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'failed':
        return <XCircleIcon className="w-4 h-4" />;
      case 'pending':
        return <ClockIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Appointment Reminder Entry
              </h2>
              <p className="text-sm text-gray-500">
                Entry ID: {entry?.id ? entry.id.slice(-8) : 'N/A'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Patient Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Patient Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient Name *
                </label>
                <input
                  type="text"
                  value={formData.patient_name}
                  onChange={(e) => handleInputChange('patient_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.patient_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter patient name"
                />
                {validationErrors.patient_name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.patient_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.phone_number ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter phone number"
                />
                {validationErrors.phone_number && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.phone_number}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District Code
                </label>
                <input
                  type="text"
                  value={formData.district_code}
                  onChange={(e) => handleInputChange('district_code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter district code"
                />
              </div>
            </div>

            {/* Appointment Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Appointment Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Appointment Date *
                </label>
                <input
                  type="date"
                  value={formData.appointment_date}
                  onChange={(e) => handleInputChange('appointment_date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.appointment_date ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.appointment_date && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.appointment_date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={formData.appointment_start_time}
                  onChange={(e) => handleInputChange('appointment_start_time', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.appointment_start_time ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.appointment_start_time && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.appointment_start_time}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes) *
                </label>
                <input
                  type="number"
                  value={formData.appointment_duration}
                  onChange={(e) => handleInputChange('appointment_duration', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.appointment_duration ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter duration in minutes"
                  min="1"
                />
                {validationErrors.appointment_duration && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.appointment_duration}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send Status
                </label>
                <select
                  value={formData.send_status}
                  onChange={(e) => handleInputChange('send_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>
            </div>

            {/* Doctor & Clinic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Doctor & Clinic Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Doctor Name
                </label>
                <input
                  type="text"
                  value={formData.doctor_name}
                  onChange={(e) => handleInputChange('doctor_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter doctor name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clinic Name
                </label>
                <input
                  type="text"
                  value={formData.clinic_name}
                  onChange={(e) => handleInputChange('clinic_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter clinic name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clinic Address
                </label>
                <textarea
                  value={formData.clinic_address}
                  onChange={(e) => handleInputChange('clinic_address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter clinic address"
                  rows="3"
                />
              </div>
            </div>

            {/* References & Template */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">References & Template</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Doctor ID
                </label>
                <input
                  type="text"
                  value={formData.reference_doctor_id}
                  onChange={(e) => handleInputChange('reference_doctor_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter reference doctor ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Clinic ID
                </label>
                <input
                  type="text"
                  value={formData.reference_clinic_id}
                  onChange={(e) => handleInputChange('reference_clinic_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter reference clinic ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Appointment ID
                </label>
                <input
                  type="text"
                  value={formData.reference_appointment_id}
                  onChange={(e) => handleInputChange('reference_appointment_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter reference appointment ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={formData.template_name}
                  onChange={(e) => handleInputChange('template_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter template name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Error Message
                </label>
                <textarea
                  value={formData.error_message}
                  onChange={(e) => handleInputChange('error_message', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter error message"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter any additional remarks or notes"
                  rows="3"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAppointmentReminderJobEntryDialog;
